<template>
  <div class="top-search" ref="root">
    <div class="top-search-col">
      <form
        @submit.prevent="doSearch()"
        role="search"
        class="top-search-keyword"
        ref="trigger"
      >
        <div class="search-keyword-block">
          <input
            ref="keywordInput"
            type="text"
            class="search-keyword"
            :placeholder="ls(cs.keywordPlaceholder)"
            v-model="keyword"
          />
          <button
            type="button"
            class="input-menu jps-icon-button"
            @click="toggle()"
          >
            <BaseIcon name="mdi/history" />
          </button>
        </div>
        <div class="top-search-buttons">
          <button type="submit">
            <div>
              <BaseIcon name="mdi/magnify" :alt="ls(dapLangGlobal.search)" />
            </div>
            <span>{{ ls(dapLangGlobal.search) }}</span>
          </button>
          <button
            type="button"
            class="right-most"
            @click.stop="searchImageWithin()"
          >
            <div>
              <BaseIcon name="mdi/image-search" :alt="ls(dapLangGlobal.imageSearch)" />
            </div>
            <span>{{ ls(dapLangGlobal.imageSearch) }}</span>
          </button>
        </div>
      </form>
      <BaseToggleSwitch class="image-toggle" v-model="imageOnly">{{ ls(dapLangGlobal.imageOnly) }}</BaseToggleSwitch>
      <p class="image-toggle-note">{{ ls(dapLangGlobal.imageOnlyDescription) }}</p>
    </div>
    <div
      class="top-search-plus"
      ref="dropdownMenu"
      v-trap-focus="true"
      tabindex="-1"
      :aria-hidden="!searchActive"
    >
      <transition name="fadetab">
        <div class="tag-fields" v-if="searchActive">
          <dl>
            <dt v-if="history.length">
              {{ lss('最近の検索', 'Recent Searches') }}
            </dt>
            <dd
              v-for="(h, i) in history"
              :key="i"
              @click="searchFromHistory(h)"
            >
              <SearchQuerySpan :q="h" />
            </dd>
            <dd class="show-all" v-if="showAllHistory">
              <button @click="showHistory()">
                {{ lss('全ての履歴を表示', 'Show all histories') }}
              </button>
            </dd>
            <dt>
              {{ lss('キーワード検索を試してみる', 'Search by keyword') }}
            </dt>
            <dd
              v-for="q in cs.sampleQuery.filter(
                (q) => !q.image && !q.text2image
              )"
            >
              <BaseLink :to="searchTo(q)">
                <BaseIcon name="mdi/magnify" />
                {{ ls(q.name) }}
              </BaseLink>
            </dd>
            <template v-if="cs.id === systemCrossSearchId()">
              <dt>{{ lss('画像AI検索を試してみる', 'AI Search by image') }}</dt>
              <dd
                v-for="q in cs.sampleQuery.filter(
                  (q) => q.image || q.text2image
                )"
              >
                <BaseLink :to="searchTo(q)">
                  <BaseIcon name="mdi/image-search" />
                  {{ ls(q.name) }}
                </BaseLink>
              </dd>
              <dd>
                <a @click="selectImageSearch()">
                  <BaseIcon name="mdi/image-search" />
                  {{
                    lss(
                      '類似画像を選んで検索',
                      'Select image for similar search'
                    )
                  }}
                </a>
              </dd>
            </template>
          </dl>
          <SearchFieldImageFileModal ref="searchFieldImageFileModal" />
          <SearchHistoryModal ref="searchHistoryModal" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import { createPopper } from '@popperjs/core'
import vTrapFocus from '~/directives/trap-focus'
import type { SearchFieldImageFileModal, SearchHistoryModal } from '#components'

const { ls, lss, langPath } = useI18n()
const localSearchStore = useLocalSearchStore()
const router = useRouter()

interface Props {
}
const props = withDefaults(defineProps<Props>(), {
})

const cs = systemCrossSearchCs()

const root: Ref<HTMLElement> = ref()
const keywordInput: Ref<HTMLElement> = ref()
const trigger: Ref<HTMLElement> = ref()
const dropdownMenu: Ref<HTMLElement> = ref()

let popperJS
onMounted(() => {
  nextTick(() => {
    keywordInput.value.focus()
  })
  popperJS = createPopper(
      trigger.value,
      dropdownMenu.value,
      {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
          {
            name: 'computeStyles',
            options: {
              gpuAcceleration: true,
              adaptive: false,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [16, 2],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom'],
            },
          },
        ],
      }
  )
  document.addEventListener('keydown', keydownEscape)
  document.addEventListener('click', handleDocumentClick)
})

const searchActive: Ref<boolean> = ref(false)
const imageOnly: Ref<boolean> = ref(false)

const handleDocumentClick = (e: any) => {
  if (!root.value || elementContains(root.value, e.target)) {
    return
  }
  searchActive.value = false
}

const elementContains = (elm, otherElm) => {
  if (typeof elm.contains === 'function') {
    return elm.contains(otherElm)
  }
  return false
}

const keydownEscape = (e) => {
  if (e.key === 'Escape') {
    close()
  }
}

const toggle = () => {
  if (!searchActive.value) {
    searchActive.value = true
    nextTick(() => {
      popperJS.update()
    })
  } else {
    searchActive.value = false
  }
}

const close = () => {
  searchActive.value = false
}

onUnmounted(() => {
  document.removeEventListener('keydown', keydownEscape)
  document.removeEventListener('click', handleDocumentClick)
  if (popperJS) {
    popperJS.destroy()
    popperJS = null
  }
})

//検索実行
const keyword: Ref<string> = ref('')

const history = computed(() => {
  return localSearchStore.history.slice(0, 5)
})

const showAllHistory = computed(() => {
  return localSearchStore.history.length > 5
})

const searchFieldImageFileModal = ref<InstanceType<typeof SearchFieldImageFileModal>>()
const selectImageSearch = async () => {
  searchActive.value = false
  try {
    const result = await searchFieldImageFileModal.value.openAwait()
    if (!result) return
    router.push(langPath(`/csearch/${systemCrossSearchId()}?csid=${systemCrossSearchId()}&image=${result.id}`))
  } catch (cancel) {}
}

// const searchImageWithin = async () => {
const searchImageWithin = () => {
  searchActive.value = false
  router.push(langPath(`/csearch/${systemImageSearchId()}${toSearchQueryString({
    csid: systemImageSearchId(),
    keyword: split(keyword.value)
  })}`))
}

const doSearch = () => {
  searchActive.value = false
  router.push(langPath(`/csearch/${systemCrossSearchId()}${toSearchQueryString({
    csid: systemCrossSearchId(),
    keyword: split(keyword.value),
    filter: {
      contents: imageOnly.value ? ['+internet'] : undefined
    }
  })}`))
}

const searchFromHistory = (q: SearchQuery) => {
  router.push(langPath(`/csearch/${q.csid}${toSearchQueryString(q)}`))
}

const searchHistoryModal = ref<InstanceType<typeof SearchHistoryModal>>()
const showHistory = async () => {
  try {
    await searchHistoryModal.value.openAwait()
  } catch (cancel) {}
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.top-search {
  width: 100%;
  position: relative;
  .top-search-keyword {
    width: 100%;
    display: flex;
    align-items: center;
    @include is-mobile {
      gap: 6px;
    }
    @include is-desktop {
      gap: 6px;
    }
    .search-keyword-block {
      flex-grow: 1;
      position: relative;
      width: 100%;
      flex: 1 1 auto;
      min-width: 0;
      .search-keyword {
        appearance: none;
        width: 100%;
        outline: 0;
        border: none;
        padding-left: calc(1rem - 1px);
        background: rgba(0, 0, 0, 0);
        background-color: $white;
        padding-right: 2rem;
        @include is-mobile {
          height: 3.5rem;
          font-size: 0.7rem;
          border-radius: 1.75rem 3px 3px 1.75rem;
        }
        @include is-desktop {
          height: 3.5rem;
          font-size: 1rem;
          border-radius: 1.75rem 3px 3px 1.75rem;
        }
        &:focus {
          transition: all 250ms ease-in-out;
          box-shadow: inset 0px 0px 4px 0px $jps-gray-600;
        }
      }
      .input-menu {
        position: absolute;
        right: 5px;
        height: 54px;
      }
    }
    .top-search-buttons {
      display: flex;
      width: 17.5rem;
      @include is-mobile {
        gap: 6px;
      }
      @include is-desktop {
        gap: 6px;
      }

      button {
        @include no-decorative-button();
        flex: 1 1 0;
        border-radius: 6px;
        overflow: hidden;
        background-color: $jps-key-blue;
        border: 1px solid $white;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        @include is-mobile {
          height: 3.5rem;
          padding: 1rem .125rem;
        }
        @include is-desktop {
          height: 3.5rem;
          padding: 1rem .75rem;
        }
        div {
          color: $white;
        }
        .jps-icon {
          font-size: 1.5rem;
          margin: 0;

          @include is-mobile() {
            font-size: 1.75rem;
          }

          color: $white;
        }
        span {
          color: $white;
          font-size: 0.75rem;
          min-width: 3rem;
          @include is-mobile {
            font-size: 0.65rem;
          }
        }
        &.right-most {
          border-top-right-radius: 2rem;
          border-bottom-right-radius: 2rem;
          padding: 1rem 1.25rem 1rem .75rem;
        }
      }
    }

    .top-search-button {
      gap: 4px;
    }

    .jps-top-search-close {
      .jps-icon {
        color: $white;
        font-size: 1.75rem;
      }
    }
  }

  .top-search-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    .image-toggle {
      color: $white;
    }
    .image-toggle-note {
      color: $white;
      font-size: 0.75rem;
      text-align: center;
      margin: 0;
    }
  }

  .top-search-plus {
    z-index: 999;
    .tag-fields {
      border-radius: 0.25rem;
      font-size: 0.875rem !important;
      padding-top: 0.5rem;
      overflow-y: auto;
      max-height: 400px;

      @include is-mobile() {
        box-shadow: 0 0.25rem 1rem 0.5rem rgba(0, 0, 0, 0.3);
        background-color: $white;
        width: 80vw;
      }
      @include is-tablet() {
        box-shadow: 0 0.25rem 1rem 0.5rem rgba(0, 0, 0, 0.3);
        background-color: $white;
        width: 360px;
      }

      @include custom-scroll-bar();

      .jps-query-span {
        overflow-x: hidden;
      }

      .show-all {
        button {
          width: 100%;
          text-align: left;
          font-size: 0.75rem !important;
          @include no-decorative-button();
        }
      }

      dl {
        color: $black;
        line-height: 2em;
        padding-bottom: 0.5rem;

        @include is-tablet() {
          width: 20.5rem;
        }

        dt {
          padding: 0 0.75rem;
          font-size: 0.75rem !important;
        }

        dd {
          padding: 0.125rem 1rem;
          font-size: 1rem !important;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &:hover {
            background-color: $jps-gray-100;

            @include is-mobile() {
              background-color: $white;
            }
          }

          cursor: pointer;

          a {
            width: 100%;
            display: flex;
            align-items: center;
            color: $black;

            .icon {
              margin-right: 1rem;
            }

            &.is-disabled {
              pointer-events: none;
              color: $jps-gray-500;
            }
          }

          svg {
            margin-right: 1rem;
            color: $jps-gray-600;
          }
        }
      }

      ul {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        border-top: 1px solid $jps-gray-300;
        height: 3rem;
        padding: 0.5rem;

        li {
          padding: 0.5rem;

          a {
            color: $jps-key-blue;
          }
        }
      }
    }
  }
}
</style>
